import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useApi } from '../hooks/useSimpleApi';

// Types
interface User {
  email: string;
  view_id: string;
  customer_name?: string;
  is_locked: boolean;
}

interface PermissionInfo {
  mode: 'open' | 'closed';
  is_authenticated: boolean;
  is_admin: boolean;
  can_access_admin: boolean;
  can_access_all_views: boolean;
  allowed_views: string[] | null;
  user_email: string | null;
  user_domain: string | null;
}

interface AuthContextValue {
  // Auth Mode
  mode: 'open' | 'closed';

  // User State
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: User | null;

  // Permissions
  allowedViews: string[] | null; // null = all views allowed

  // Loading State
  loading: boolean;
  error: string | null;

  // Methods
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;

  // Permission Checks
  canAccessView: (viewId: string) => boolean;
  canAccessAdmin: () => boolean;
  canAccessAllViews: () => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<'open' | 'closed'>('open');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [allowedViews, setAllowedViews] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const api = useApi();

  // Initialize auth state
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if auth is enabled
      const statusResponse = await api.get('/api/auth/customer/status');
      const authEnabled = statusResponse.data?.enabled || false;
      setMode(authEnabled ? 'closed' : 'open');

      if (!authEnabled) {
        // Open mode - everyone is "admin"
        setIsAuthenticated(false);
        setIsAdmin(true);
        setAllowedViews(null); // All views allowed
        setLoading(false);
        return;
      }

      // Closed mode - check token
      const token = localStorage.getItem('customer_token');

      if (!token) {
        setIsAuthenticated(false);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Verify token and get user info
      try {
        const verifyResponse = await api.get('/api/auth/customer/verify', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const userData = verifyResponse.data as User;
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);

          // Store user info
          localStorage.setItem('customer_email', userData.email);
          localStorage.setItem('customer_view', userData.view_id);
          if (userData.customer_name) {
            localStorage.setItem('customer_name', userData.customer_name);
          }

          // Check if locked
          if (userData.is_locked) {
            await logout();
            setError('Ihr Konto wurde gesperrt');
            return;
          }

          // Get permission info
          const permResponse = await api.get('/api/views/permissions/info', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          const permissions = permResponse.data as PermissionInfo;
          setIsAdmin(permissions.is_admin);
          setAllowedViews(permissions.allowed_views);

          console.log('🔐 Auth initialized:', {
            mode: permissions.mode,
            isAdmin: permissions.is_admin,
            email: permissions.user_email,
            allowedViews: permissions.allowed_views
          });
        } else {
          // Invalid token
          await logout();
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        await logout();
      }
    } catch (err) {
      console.error('Auth status check failed:', err);
      // Assume open mode if backend is unreachable
      setMode('open');
      setIsAdmin(true);
      setAllowedViews(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);

      const response = await api.post('/api/auth/customer/login', {
        email,
        password
      });

      if (response.data?.token) {
        // Store token
        localStorage.setItem('customer_token', response.data.token);
        localStorage.setItem('customer_email', email);
        localStorage.setItem('customer_view', response.data.view_id);

        if (response.data.customer_name) {
          localStorage.setItem('customer_name', response.data.customer_name);
        }

        // Refresh auth state
        await checkAuthStatus();

        return true;
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.response?.data?.detail || 'Login fehlgeschlagen');
    }

    return false;
  };

  const logout = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('customer_token');

      if (token) {
        // Notify backend
        await api.post('/api/auth/customer/logout', {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local state
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_email');
      localStorage.removeItem('customer_view');
      localStorage.removeItem('customer_name');

      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      setAllowedViews(null);
    }
  };

  const refreshAuth = async (): Promise<void> => {
    await checkAuthStatus();
  };

  // Permission check methods
  const canAccessView = (viewId: string): boolean => {
    // Open mode or admin: can access all
    if (mode === 'open' || isAdmin) {
      return true;
    }

    // Check allowed views
    if (allowedViews === null) {
      return true; // null means all views allowed
    }

    return allowedViews.includes(viewId);
  };

  const canAccessAdmin = (): boolean => {
    return mode === 'open' || isAdmin;
  };

  const canAccessAllViews = (): boolean => {
    return mode === 'open' || isAdmin || allowedViews === null;
  };

  const value: AuthContextValue = {
    mode,
    isAuthenticated,
    isAdmin,
    user,
    allowedViews,
    loading,
    error,
    login,
    logout,
    refreshAuth,
    canAccessView,
    canAccessAdmin,
    canAccessAllViews
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
